import React, { useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Edit, QrCode, Printer, Download } from 'lucide-react';
import { QRPreviewModal } from '../shared/WizardComponents';

export const ShowerSchedulesSection = ({
  filteredShowerSchedules = [],
  filteredShowerTypes = [],
  filteredShowerHardwareSets = [],
  selectedShowerScheduleIds = [],
  canEditCSIItems,
  operationLoading,
  uploadingExcel,
  // Filter props
  search = '',
  onSearchChange,
  filters = { type: '', location: '' },
  onFiltersChange,
  sortBy = 'createdAt_desc',
  onSortChange,
  onClearFilters,
  // Action handlers
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onAddNew,
  onEdit,
  onDelete,
  onOpenDetail,
  onPrintLabel,
  onPrintAllQR,
  onExcelUpload,
}) => {
  const [qrPreview, setQrPreview] = useState({ open: false, qrToken: null, label: '' });

  const displaySchedules = useMemo(() => {
    let result = [...filteredShowerSchedules];
    const searchTerm = (search || '').toLowerCase().trim();
    if (searchTerm) {
      result = result.filter(s => {
        const label = (s.label || '').toLowerCase();
        const location = (s.location || '').toLowerCase();
        return label.includes(searchTerm) || location.includes(searchTerm);
      });
    }
    if (filters.type) {
      result = result.filter(s => s.showerTypeId === filters.type);
    }
    if (filters.location) {
      const locTerm = filters.location.toLowerCase().trim();
      result = result.filter(s => (s.location || '').toLowerCase().includes(locTerm));
    }
    result.sort((a, b) => {
      switch (sortBy) {
        case 'label_asc': return (a.label || '').localeCompare(b.label || '');
        case 'label_desc': return (b.label || '').localeCompare(a.label || '');
        case 'createdAt_asc': return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case 'createdAt_desc':
        default: return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });
    return result;
  }, [filteredShowerSchedules, search, filters, sortBy]);

  const hasActiveFilters = search || filters.type || filters.location || sortBy !== 'createdAt_desc';

  const getShowerTypeName = (typeId) => {
    const st = filteredShowerTypes.find(t => t.id === typeId);
    return st ? st.name : '—';
  };

  const getHardwareSetName = (hsId) => {
    if (!hsId) return '—';
    const hs = filteredShowerHardwareSets.find(h => h.id === hsId);
    return hs ? hs.name : '—';
  };

  return (
    <div data-testid="shower-schedules-section">
      {/* Action Bar - Windows pattern */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Shower Schedule</h2>
        <div className="flex gap-2">
          {canEditCSIItems && filteredShowerTypes.length > 0 && (
            <>
              <Button
                variant="outline"
                onClick={onSelectAll}
                disabled={filteredShowerSchedules.length === 0}
                data-testid="shower-schedule-select-all-btn"
              >
                {selectedShowerScheduleIds.length === filteredShowerSchedules.length && filteredShowerSchedules.length > 0 ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                onClick={onConfirmBulkDelete}
                disabled={selectedShowerScheduleIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                data-testid="shower-schedule-bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedShowerScheduleIds.length})
              </Button>
              <Button
                variant="outline"
                onClick={onPrintAllQR}
                disabled={filteredShowerSchedules.length === 0}
                data-testid="shower-schedule-print-all-qr-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All QR Labels
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById('excel-upload-shower').click()}
                disabled={uploadingExcel}
                data-testid="shower-schedule-import-excel-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {uploadingExcel ? 'Uploading...' : 'Import from Excel'}
              </Button>
              <input
                id="excel-upload-shower"
                type="file"
                accept=".xlsx,.xls"
                onChange={onExcelUpload}
                style={{ display: 'none' }}
              />
              <Button onClick={onAddNew} disabled={operationLoading} data-testid="shower-schedule-add-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Shower Schedule
              </Button>
            </>
          )}
          {canEditCSIItems && filteredShowerTypes.length === 0 && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              Create a Shower Type first to add schedules
            </div>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6" data-testid="shower-schedule-filter-bar">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search by shower label..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            data-testid="shower-schedule-search-input"
          />
        </div>
        <div className="flex gap-2">
          <Select
            value={filters.type || 'all'}
            onValueChange={(value) => onFiltersChange({ ...filters, type: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="w-48" data-testid="shower-schedule-type-filter">
              <SelectValue placeholder="Filter by Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {filteredShowerTypes.map((st) => (
                <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger className="w-48" data-testid="shower-schedule-sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="createdAt_desc">Newest First</SelectItem>
              <SelectItem value="createdAt_asc">Oldest First</SelectItem>
              <SelectItem value="label_asc">Label (A-Z)</SelectItem>
              <SelectItem value="label_desc">Label (Z-A)</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="text"
            placeholder="Filter by location..."
            value={filters.location}
            onChange={e => onFiltersChange({ ...filters, location: e.target.value })}
            className="w-48"
            data-testid="shower-schedule-location-filter"
          />
          <Button variant="outline" onClick={onClearFilters} data-testid="shower-schedule-clear-filters-btn">
            Clear
          </Button>
        </div>
      </div>

      {/* Schedule List */}
      {displaySchedules.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12" data-testid="shower-schedule-empty">
            <p className="text-slate-600">
              {hasActiveFilters ? 'No schedules match your search/filters' : 'No shower schedules created yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {canEditCSIItems && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-10"></th>
                    )}
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Shower Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Hardware Set</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">QR</th>
                    {canEditCSIItems && (
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {displaySchedules.map(schedule => {
                    const showerType = filteredShowerTypes.find(t => t.id === schedule.showerTypeId);
                    return (
                      <tr key={schedule.id} className={`hover:bg-slate-50 ${selectedShowerScheduleIds.includes(schedule.id) ? 'bg-blue-50' : ''}`} data-testid={`shower-schedule-row-${schedule.id}`}>
                        {canEditCSIItems && (
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={selectedShowerScheduleIds.includes(schedule.id)}
                              onCheckedChange={(checked) => onToggleSelection(schedule.id, { target: { checked } })}
                            />
                          </td>
                        )}
                        <td className="px-3 py-2 font-medium text-blue-600 hover:text-blue-800 cursor-pointer" onClick={() => onOpenDetail(schedule)}>
                          {schedule.label}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          <div className="flex items-center gap-2">
                            {showerType && (
                              <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: showerType.markerColor || '#10B981' }} />
                            )}
                            {getShowerTypeName(schedule.showerTypeId)}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{getHardwareSetName(schedule.hardwareSetId)}</td>
                        <td className="px-3 py-2 text-slate-600">{schedule.location || '-'}</td>
                        <td className="px-3 py-2 text-center">
                          {schedule.qrToken ? (
                            <button
                              type="button"
                              className="cursor-pointer hover:opacity-70 transition-opacity inline-block"
                              onClick={() => setQrPreview({ open: true, qrToken: schedule.qrToken, label: schedule.label })}
                              title="Click to enlarge QR code"
                              data-testid={`shower-schedule-qr-${schedule.id}`}
                            >
                              {schedule.qrCodeUrl ? (
                                <img src={schedule.qrCodeUrl} alt={`QR ${schedule.label}`} className="w-8 h-8 mx-auto" />
                              ) : (
                                <QrCode className="h-5 w-5 text-slate-400 mx-auto" />
                              )}
                            </button>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        {canEditCSIItems && (
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-end gap-1">
                              {onPrintLabel && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onPrintLabel(schedule.id)} data-testid={`shower-schedule-print-${schedule.id}`}>
                                  <Printer className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(schedule)} data-testid={`shower-schedule-edit-${schedule.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => onDelete(schedule.id)} data-testid={`shower-schedule-delete-${schedule.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <QRPreviewModal
        open={qrPreview.open}
        onClose={() => setQrPreview({ open: false, qrToken: null, label: '' })}
        qrToken={qrPreview.qrToken}
        label={qrPreview.label}
      />
    </div>
  );
};

export default ShowerSchedulesSection;
